import { useState } from "react";
import { Link } from "wouter";
import { useContainers, useRestartContainer, useStartContainer, useStopContainer } from "../../api/queries";
import type { Container } from "../../api/types";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { EmptyState, ErrorState, LoadingState } from "../../components/AsyncStates";
import { Modal } from "../../components/Modal";
import { ContainerManagedPill, ContainerStatePill } from "../../components/StatePill";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { useToast } from "../../toast/ToastContext";
import { RestartIcon, StartIcon, StopIcon } from "./containerActionIcons";
import "../../components/Form.css";
import "./host-detail.css";

interface MachineContainersSectionProps {
  nodeId: string;
  /** `container:update` (operator/admin only, server/internal/authz/model.go
   * — S-8/S-9). UX-only gate; the server enforces the real decision on every
   * request regardless of what this prop says. */
  canManage: boolean;
}

/**
 * The Fleet → machine detail containers table (IA slice 2 §8): per-container
 * restart/stop/start for the Platform Lead's surgical cases, one machine at a
 * time — distinct from the service-level lifecycle controls on Operate.
 *
 * ONLY wires verbs with a real web-reachable route: `POST
 * /api/containers/{nodeId}/{containerId}/{start|stop|restart}`
 * (server/internal/nodesvc's `ContainerActionHandler`, `isAllowedAction`).
 * `container:delete` is allow-listed at the Docker authz proxy and in the
 * authz matrix, but NO handler/route exposes it through this API surface —
 * so there is deliberately no Delete button here (A-10: no button this app
 * cannot actually carry out). See the IA slice 2 handoff notes for the gap.
 */
export function MachineContainersSection({ nodeId, canManage }: MachineContainersSectionProps) {
  const { pushToast } = useToast();
  const paginator = usePaginator(nodeId);
  const { data, isPending, isError, error, refetch, isFetching } = useContainers({
    nodeId,
    cursor: paginator.cursor,
  });
  const startMutation = useStartContainer();
  const stopMutation = useStopContainer();
  const restartMutation = useRestartContainer();
  const [stopTarget, setStopTarget] = useState<Container | null>(null);

  const roleHint = canManage ? undefined : "Viewers can't manage containers.";

  function runAction(kind: "start" | "restart", container: Container) {
    const mutation = kind === "start" ? startMutation : restartMutation;
    const verb = kind === "start" ? "started" : "restarted";
    mutation.mutate(
      { nodeId: container.nodeId, containerId: container.id },
      {
        onSuccess: () => pushToast({ title: `${container.name} ${verb}.`, tone: "success" }),
        onError: (err) =>
          pushToast({
            title: `Couldn't ${kind} ${container.name}`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          }),
      },
    );
  }

  function confirmStop() {
    if (!stopTarget) return;
    const container = stopTarget;
    stopMutation.mutate(
      { nodeId: container.nodeId, containerId: container.id },
      {
        onSuccess: () => {
          pushToast({ title: `${container.name} stopped.`, tone: "success" });
          setStopTarget(null);
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't stop ${container.name}`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
          setStopTarget(null);
        },
      },
    );
  }

  const columns: DataTableColumn<Container>[] = [
    { key: "state", header: "State", cell: (c) => <ContainerStatePill state={c.state} /> },
    {
      key: "name",
      header: "Name",
      cell: (c) => (
        <Link className="data-table__primary-link" href={`/containers/${c.nodeId}/${c.id}`}>
          {c.name}
        </Link>
      ),
      className: "data-table__primary",
    },
    { key: "managed", header: "", cell: (c) => <ContainerManagedPill managed={c.managed} /> },
    { key: "image", header: "Image", cell: (c) => c.image, className: "data-table__mono" },
    { key: "status", header: "Status", cell: (c) => c.status, className: "data-table__secondary" },
    {
      key: "actions",
      header: "",
      cell: (c) => {
        const canStart = c.state === "exited" || c.state === "created" || c.state === "dead";
        const canStop = c.state === "running" || c.state === "paused" || c.state === "restarting";
        const canRestart = c.state === "running" || c.state === "paused";
        const pending =
          (startMutation.isPending && startMutation.variables?.containerId === c.id) ||
          (restartMutation.isPending && restartMutation.variables?.containerId === c.id) ||
          (stopMutation.isPending && stopMutation.variables?.containerId === c.id);
        return (
          <div className="machine-containers__actions">
            {canRestart && (
              <Button
                type="button"
                variant="secondary"
                aria-label="Restart"
                title={roleHint}
                disabled={!canManage || pending}
                onClick={() => runAction("restart", c)}
              >
                <RestartIcon />
              </Button>
            )}
            {canStart && (
              <Button
                type="button"
                variant="secondary"
                aria-label="Start"
                title={roleHint}
                disabled={!canManage || pending}
                onClick={() => runAction("start", c)}
              >
                <StartIcon />
              </Button>
            )}
            {canStop && (
              <Button
                type="button"
                variant="secondary"
                aria-label="Stop"
                title={roleHint}
                disabled={!canManage || pending}
                onClick={() => setStopTarget(c)}
              >
                <StopIcon />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  if (isPending) {
    return <LoadingState label="Loading containers…" />;
  }

  if (isError) {
    return (
      <ErrorState
        description={error instanceof Error ? error.message : "Couldn't load containers on this machine."}
        onRetry={refetch}
      />
    );
  }

  if (!data || data.items.length === 0) {
    return <EmptyState title="No containers on this machine" description="Nothing is running here yet." />;
  }

  return (
    <>
      <DataTable columns={columns} rows={data.items} rowKey={(c) => c.id} ariaBusy={isFetching} />
      <PaginatorControls itemCount={data.items.length} nextCursor={data.nextCursor} paginator={paginator} noun="containers" />

      <Modal open={stopTarget !== null} title={`Stop "${stopTarget?.name}"?`} onClose={() => setStopTarget(null)}>
        <p>This stops the container on this machine. Anything talking to it will lose the connection.</p>
        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={() => setStopTarget(null)} disabled={stopMutation.isPending}>
            Cancel
          </Button>
          <Button type="button" variant="danger" onClick={confirmStop} disabled={stopMutation.isPending}>
            {stopMutation.isPending ? "Stopping…" : "Stop container"}
          </Button>
        </div>
      </Modal>
    </>
  );
}
